export const DEFAULT_DOLLAR_CAP = 25000;

export const ASSURANCE_RATING_MAX = 95;
export const ASSURANCE_RATING_MIN = 0;
export const DEFAULT_ASSURANCE_RATING = 80;

export const LOW_ASSURANCE_DISPLAY_AMOUNTS = {
  ESTIMATE: 'estimate',
  FULL_COST: 'full-cost'
};

export const LOW_ASSURANCE_MESSAGE_MAX_LENGTH = 500;

export const CARD_ON_FILE_REQUIRED_ROUTE = 'require-card-on-file';

export const LOW_ASSURANCE_RESPONSE_CODES = {
  PERMISSION_DENIED: 2,
  CARD_ON_FILE_REQUIRED: 24
};

export const CARD_ON_FILE_REQUIRED_MESSAGE =
  'A card on file is required for this visit while coverage is being verified. Pay by card or save a card first.';

export const DEFAULT_LOW_ASSURANCE = {
  enabled: false,
  display_amount: LOW_ASSURANCE_DISPLAY_AMOUNTS.ESTIMATE,
  card_on_file: { route_to: 'patient-option' },
  authorization_applied: { route_to: 'all-visits-this-year' },
  message:
    'Your insurance coverage is still being verified. Any overpayment will be refunded; any remaining balance will be charged according to your pre-authorization.'
};

export const PAYMENT_METHOD_FIELDS = {
  CASH: 'cash',
  CHECK: 'check',
  CREDIT_DEBIT_CARD: 'credit-card',
  ACH: 'ach',
  PRE_AUTH: 'pre-auth',
  RECURRING_CHARGE: 'recurring-charge',
  DEFER_PAYMENT: 'defer-payment'
};

// Picked at check-in but settled in person, so they defer the balance instead of charging it.
export const DEFERRED_PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHOD_FIELDS.CASH]: 'cash',
  [PAYMENT_METHOD_FIELDS.CHECK]: 'check'
};

export const DEFERRED_PAYMENT_METHOD_FIELDS = Object.keys(DEFERRED_PAYMENT_METHOD_LABELS);

export const isDeferredPaymentMethod = (method) => DEFERRED_PAYMENT_METHOD_FIELDS.includes(method);

export const getDeferredPaymentTitle = (method) => {
  const label = DEFERRED_PAYMENT_METHOD_LABELS[method];
  return label
    ? `Patient selected they want to pay with ${label} in appointment`
    : 'Patient deferred payment';
};

export const getDeferredPaymentMessage = (method) => {
  const label = DEFERRED_PAYMENT_METHOD_LABELS[method];
  return label
    ? `No payment was collected at check-in. Collect the ${label} payment at the appointment and record it here so the balance stays accurate.`
    : 'This increases the risk of non-payment. To minimize billing costs and losses, use the AEOB to clarify costs, set expectations, and, whenever possible, secure payment or keep a card on file before starting treatment.';
};

export const PHASE_FIELDS = {
  IN_NETWORK: 'in_network',
  OUT_OF_NETWORK: 'out_of_network',
  SELF_PAY: 'self_pay'
};

export const LOW_ASSURANCE_FIELD = 'low_assurance';

export const RECURRING_CHARGE_FREQUENCY_VALUES = ['weekly', 'bi-weekly', 'monthly'];

// Plans are stored in months but charged in weeks, so a calendar year (52 weeks) is the basis —
// 4 weeks/month would lose four weeks a year and undercount the installments.
export const WEEKS_PER_MONTH = 52 / 12;

export const DEFAULT_RECURRING_CHARGE_CONFIG = {
  require_approval: true,
  min_amount: 2500,
  frequencies: RECURRING_CHARGE_FREQUENCY_VALUES,
  // Kept in sync with the first selected frequency: the plan builder starts a plan on one cadence.
  frequency: 'weekly',
  duration_months: 6,
  offer_on_kiosk: false
};

const DEFAULT_OVERRIDE_CONFIG = {
  card_on_file: { route_to: 'patient-option' },
  authorization_applied: { route_to: 'all-visits-this-year' },
  charge_limit: {
    route_to: 'combined-allowable-amount',
    config: { dollar_cap: DEFAULT_DOLLAR_CAP }
  }
};

export const DEFAULT_PAYER_OVERRIDE = { payers: [], ...DEFAULT_OVERRIDE_CONFIG };
export const DEFAULT_TAG_OVERRIDE = { tags: [], ...DEFAULT_OVERRIDE_CONFIG };

export const OVERRIDE_FIELDS = {
  PAYER: 'payer_override',
  TAG: 'tag_override'
};

export const OVERRIDE_PRECEDENCE = {
  [OVERRIDE_FIELDS.PAYER]: 10,
  [OVERRIDE_FIELDS.TAG]: 20
};

export const LETTER_TYPES = {
  GOOD_FAITH_ESTIMATE: 'good-faith-estimate',
  PATIENT_OPTION_LETTER: 'patient-option-letter',
  TOPS_GFE_POL: 'tops-gfe-pol'
};

export const TOPS_PAYMENT_OPTION_FIELDS = {
  RESERVATION_SAVINGS: 'reservation_savings',
  PAYMENT_PLAN: 'payment_plan',
  HALF_DOWN: 'half_down'
};

export const TOPS_DISCOUNT_MIN = 0;
export const TOPS_DISCOUNT_MAX = 100;
export const TOPS_FIRST_PAYMENT_MIN = 1;
export const TOPS_FIRST_PAYMENT_MAX = 99;
export const TOPS_PLAN_MONTHS_MIN = 6;
export const TOPS_PLAN_MONTHS_MAX = 120;

export const DEFAULT_TOPS_PAYMENT_OPTIONS = {
  [TOPS_PAYMENT_OPTION_FIELDS.RESERVATION_SAVINGS]: { enabled: true, discount_percent: 5 },
  [TOPS_PAYMENT_OPTION_FIELDS.PAYMENT_PLAN]: { enabled: true, max_months: 60 },
  [TOPS_PAYMENT_OPTION_FIELDS.HALF_DOWN]: { enabled: true, first_payment_percent: 50 }
};
